import { useState, useEffect, ElementType as ReactElementType } from "react";
import {
	DraggableProvidedDragHandleProps,
	DraggableProvidedDraggableProps,
	DraggableStateSnapshot,
} from "react-beautiful-dnd";
import {
	AlertProps,
	Button,
	Dropdown,
	Icon,
	IconButton,
	SearchSelectOptionProps,
} from "@alm/flow-ds/components";
import {
	MoveData,
	propertyType,
	UpdateData,
	ElementValidation,
	FormEntity,
	FormLayout,
} from "hooks/FormDesigner/useElementList";
import { useTranslation } from "react-i18next";
import DeleteDialog from "components/FormDesigner/DeleteDialog";
import { ElementType, ElementSchema, PopulatesData } from "schemas/elements";
import elementSchema from "schemas";
import { ComponentMap } from "components/FormDesigner/Properties/properties";
import runValidation from "../../../validation/validation";
import { v4 as uuidv4 } from "uuid";

interface Props {
	isEmu?: boolean;
	index: number;
	moveElement: (data: MoveData) => void;
	removeElement: (data: number) => void;
	updateElement: (data: UpdateData) => void;
	updateElementValidation: (data: ElementValidation) => void;
	item: ElementType;
	formEntity: FormEntity;
	fullWidth: FormLayout["fullWidth"];
	draggable: DraggableProvidedDraggableProps;
	dragHandle?: DraggableProvidedDragHandleProps;
	innerRef: (element?: HTMLElement | null | undefined) => void;
	snapshot: DraggableStateSnapshot;
	lastChild?: boolean;
	addAlert?: (alert: string, status: AlertProps["status"]) => void;
	populatesData?: PopulatesData[];
	legacySearchField?: SearchSelectOptionProps;
	isSearchView?: boolean;
	hideMultiInputText?: boolean;
	tableNested?: boolean;
}

export default ({
	isEmu,
	item,
	formEntity,
	fullWidth,
	draggable,
	dragHandle,
	innerRef,
	snapshot,
	index,
	moveElement,
	removeElement,
	updateElement,
	updateElementValidation,
	lastChild,
	addAlert,
	populatesData,
	legacySearchField,
	isSearchView,
	hideMultiInputText,
	tableNested,
}: Props) => {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [elemTitle, setElemTitle] = useState<string | null>(
		item.name !== undefined ? item.name : null
	);
	const [canPopulate, setCanPopulate] = useState(true);
	const [showSearch, setShowSearch] = useState(true);

	// type of element from element schema
	const thisElement = (elementSchema as ElementSchema)[item.elem];

	useEffect(() => {
		runValidation({ item, index, updateElementValidation });
	}, [index, item, updateElementValidation]);

	useEffect(() => {
		if (
			legacySearchField !== null &&
			legacySearchField !== undefined &&
			canPopulate &&
			legacySearchField.value === item.name
		) {
			setCanPopulate(false);
		} else if (!canPopulate) setCanPopulate(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [legacySearchField, item.name]);

	useEffect(() => {
		if (item.elem === "legacyArrayView") {
			const newShowSearch =
				item.searchField !== undefined && item.searchField !== null;
			setShowSearch(newShowSearch);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [item.searchField]);

	const checkValidation = (currentElement: ElementType) => {
		let fieldsMissing = false;
		if (currentElement.formValidation === true) {
			fieldsMissing = true;
		} else {
			if (currentElement.elements)
				currentElement.elements.forEach((element: ElementType) => {
					const nested = checkValidation(element);
					if (nested) fieldsMissing = true;
				});
		}

		return fieldsMissing;
	};

	return (
		<>
			<div
				ref={innerRef}
				{...draggable}
				className={`element-card ${
					snapshot.isDragging ? " dragging" : ""
				} ${
					item.elementValidation && checkValidation(item)
						? " validation"
						: ""
				}`}>
				<div className="element-top" {...dragHandle}>
					<div className="element-heading">
						<div className="element-heading__name">
							{t(
								`formDesigner.elements.${thisElement.elem.displayName}`
							)}
						</div>
						<div className="element-heading__title">
							{elemTitle}
						</div>
					</div>
					<div>
						<IconButton
							icon="pencil"
							className={`${
								open ? "btn-primary" : "btn-basic--primary"
							}`}
							label={
								open
									? t("common.labels.close")
									: t("common.labels.edit")
							}
							onClick={() => setOpen(!open)}
						/>
						<Dropdown
							id={`elementMenu__dropdown__${index}`}
							menuBtn={
								<IconButton
									className="btn-basic--default"
									icon="ellipsis-v"
									label={t("formDesigner.icons.menuOptions")}
								/>
							}
							align="right">
							<Button
								label={t("formDesigner.labels.moveUp")}
								className="dropdown-item btn-basic--default"
								icon={<Icon type="arrow-up" />}
								onClick={() =>
									moveElement({
										initial: index,
										destination: index - 1,
									})
								}
								disabled={index === 0}
							/>
							<Button
								label={t("formDesigner.labels.moveDown")}
								className="dropdown-item btn-basic--default"
								icon={<Icon type="arrow-down" />}
								onClick={() =>
									moveElement({
										initial: index,
										destination: index + 1,
									})
								}
								disabled={lastChild}
							/>
							<Button
								label={t("common.labels.delete")}
								className="dropdown-item btn-basic--default"
								icon={<Icon type="trash" />}
								onClick={() => setModalIsOpen(true)}
							/>
						</Dropdown>
						<DeleteDialog
							isOpen={modalIsOpen}
							setIsOpen={setModalIsOpen}
							name={t(
								`formDesigner.elements.${thisElement.elem.displayName}`
							)}
							deleteElement={() => removeElement(index)}
							type="element"
						/>
					</div>
				</div>
				{open && (
					<div className="element-content">
						{Object.keys(thisElement).map(property => {
							if (
								property !== "elem" &&
								!(
									populatesData === undefined &&
									(property === "populatesFrom" ||
										property === "populatesKey")
								) &&
								!(
									item.elem === "text" &&
									(hideMultiInputText || isSearchView) &&
									(property === "limit" ||
										property === "isArray")
								) &&
								// Hide isArray for reference and lookup when inside a legacyArrayView
								!(
									(item.elem === "reference" ||
										item.elem === "lookup") &&
									property === "isArray" &&
									legacySearchField !== undefined
								) &&
								!(
									item.elem === "date" &&
									property === "allowEmptyStringAsNull" &&
									isEmu
								)
							) {
								const theProperty = property as propertyType;
								const theObject = thisElement[theProperty];
								const value = item[theProperty];
								const Property: ReactElementType = theObject
									? ComponentMap[theObject.type]
									: ComponentMap["string"];
								const props = {
									isEmu,
									value,
									validation: item.elementValidation,
									addAlert,
									key: theProperty,
									elem: item.elem,
									elementIndex: index,
									idList: formEntity.data,
									field: theProperty,
									desc: theObject ? theObject.desc : null,
									required: theObject
										? theObject.required
										: null,
									updateStore: updateElement,
									elements:
										theProperty === "searchField"
											? item.elements
											: undefined,
									optionsId:
										theProperty === "optionsPath" ||
										theProperty === "optionsFilter"
											? item.optionsId
											: undefined,
									optionsPath:
										theProperty === "optionsFilter"
											? item.optionsPath
											: undefined,
									formEntity:
										theProperty === "elements" ||
										theProperty === "search"
											? formEntity
											: undefined,
									setElemTitle:
										theProperty === "name"
											? setElemTitle
											: null,
									id: uuidv4(),
									populatesData:
										theProperty === "populatesFrom" ||
										theProperty === "populatesKey"
											? populatesData
											: undefined,
									populatesFrom:
										theProperty === "populatesKey"
											? item.populatesFrom
											: undefined,
									canPopulate:
										theProperty === "populatesFrom" ||
										theProperty === "populatesKey"
											? canPopulate
											: undefined,
									legacySearchField:
										theProperty === "elements"
											? item.searchField
											: undefined,
									showSearch:
										theProperty === "search"
											? showSearch
											: undefined,
									isSearchView:
										theProperty === "id"
											? isSearchView
											: undefined,
									hideMultiInputText:
										theProperty === "elements" &&
										item.elem === "legacyArrayView",
									tableNested:
										(theProperty === "elements" &&
											item.elem === "legacyArrayView") ||
										(theProperty ===
											"allowEmptyStringAsNull" &&
											tableNested) ||
										(theProperty === "validation" &&
											(item.elem === "reference" ||
												item.elem === "lookup") &&
											tableNested),
									fullWidth: fullWidth,
								};
								return <Property {...props} />;
							}
							return null;
						})}
					</div>
				)}
			</div>
		</>
	);
};
